import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, Save, FileText } from "lucide-react";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { Label } from "../../../foundation/altzero_base/base/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import ScopingAILayout from "../components/ScopingAILayout";
import {
  createCustomTemplate,
  updateCustomTemplate,
  loadTemplateContent,
} from "../services/documentLibraryService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import YooptaTemplateEditor from "../components/editor/YooptaTemplateEditor";
import { extractSectionTitles } from "../utils/sectionUtils";

interface LocationState {
  importedContent?: string;
  importedTitle?: string;
  importedFileName?: string;
  editTemplate?: {
    id: string;
    title: string;
    description: string;
    content: string;
    category: string;
  };
}

// 🚨 CONSISTENT H2 STANDARD: Use centralized section extraction
const extractSectionsFromMarkdown = (content: string): string[] => {
  // Use the centralized function for consistency across all components
  return extractSectionTitles(content);
};

// Simple helper to handle save operations
const handleTemplateSave = async (saveFunction: () => Promise<void>) => {
  try {
    await saveFunction();
    } catch (error) {
    console.error("❌ Template save failed:", error);
    throw error;
  }
};

export default function TemplateCreation() {
  const { user } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isImport = searchParams.get("import") === "true";
  const isEdit = searchParams.get("edit") === "true";
  const state = location.state as LocationState;

  // Form state
  const [templateForm, setTemplateForm] = useState({
    name: "",
    description: "",
    content: "",
    category: "",
  });

  // UI state
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const [isLoading, setIsLoading] = useState(false);
  const [currentSections, setCurrentSections] = useState<string[]>([]);

  // Initialize form with imported/edit data
  useEffect(() => {
    const initializeForm = async () => {
      try {
    if (isImport && state?.importedContent) {
      setTemplateForm({
        name: state.importedTitle || "",
        description: `Imported from ${state.importedFileName || "MD file"}`,
        content: state.importedContent,
        category: "",
      });
    } else if (isEdit && state?.editTemplate) {
          setIsLoading(true);
          
          try {
            // Load content from bucket instead of using state content
            console.log("🔄 Loading template content for editing:", state.editTemplate.id);
            const bucketContent = await loadTemplateContent(state.editTemplate.id);
            
      setTemplateForm({
        name: state.editTemplate.title,
        description: state.editTemplate.description,
              content: bucketContent, // Use content from bucket
        category: state.editTemplate.category || "",
      });
            
            console.log("✅ Template content loaded successfully");
          } catch (error) {
            console.error("❌ Failed to load template content:", error);
            toast({
              title: "Error",
              description: "Failed to load template content. Using fallback data.",
              variant: "destructive",
            });
            
            // Fallback to state content if bucket loading fails
            setTemplateForm({
              name: state.editTemplate.title,
              description: state.editTemplate.description,
              content: state.editTemplate.content || "",
              category: state.editTemplate.category || "",
            });
          } finally {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error("❌ Error initializing form:", error);
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [isImport, isEdit, state, toast]);

  // Update sections whenever content changes
  useEffect(() => {
    if (templateForm.content) {
      const sections = extractSectionsFromMarkdown(templateForm.content);
      setCurrentSections(sections);
    } else {
      setCurrentSections([]);
    }
  }, [templateForm.content]);

  // Handle form submission
  const handleSave = async () => {
    try {
      if (!templateForm.name.trim() || !templateForm.content.trim()) {
        toast({
          title: "Error",
          description: "Template name and content are required",
          variant: "destructive",
        });
        return;
      }

      setIsLoading(true);

      // Ensure content is properly formatted as markdown
      const markdownContent = templateForm.content.trim();

      // Extract sections from the markdown content based on headings
      const extractedSections = extractSectionsFromMarkdown(markdownContent);

      // Log the content and sections being saved for debugging
      console.log("Saving markdown content:", markdownContent);
      console.log("Extracted sections:", extractedSections);

      if (isEdit && state?.editTemplate && user?.id) {
        // Update existing template with auto-extracted sections
        await updateCustomTemplate(state.editTemplate.id, {
          name: templateForm.name.trim(),
          description: templateForm.description.trim(),
          content: markdownContent,
          category: templateForm.category.trim(),
          sections: extractedSections, // Auto-extracted sections
        }, user.id);

        toast({
          title: "Success",
          description:
            "Template updated successfully with all markdown content",
        });

        // For edit mode, stay on the same page
        console.log("✅ Template updated successfully - staying on current page");
      } else {
        // Create new template with auto-extracted sections
        const newTemplate = await createCustomTemplate({
          name: templateForm.name.trim(),
          description: templateForm.description.trim(),
          content: markdownContent,
          category: templateForm.category.trim(),
          userId: user?.id || "",
          sections: extractedSections, // Auto-extracted sections
        });

        toast({
          title: "Success",
          description:
            "Template created successfully with all markdown content",
        });

        // Check if we need to return to template selection with the new template
        const createTemplateMode = sessionStorage.getItem(
          "scopingai_createTemplateMode"
        );
        const returnToStep = sessionStorage.getItem("scopingai_returnToStep");

        if (
          createTemplateMode === "true" &&
          returnToStep === "2" &&
          newTemplate?.id
        ) {
          // Store the new template ID for selection
          sessionStorage.setItem(
            "selectedTemplateFromLibrary",
            String(newTemplate.id)
          );
          // Clear the create template mode
          sessionStorage.removeItem("scopingai_createTemplateMode");
          sessionStorage.removeItem("scopingai_returnToStep");
          // Navigate back to template selection step
          navigate("/tender/documents/new");
          return;
        }

        // 🚨 NEW: For new template creation, redirect to templates page
        console.log("✅ New template created successfully - redirecting to templates page");
        console.log("🔍 Template creation details:", {
          templateId: newTemplate?.id,
          templateName: newTemplate?.name || templateForm.name,
          redirectPath: "/tender/resource-library/documents"
        });

        // Add a small delay to ensure toast is shown before redirect
        setTimeout(() => {
          console.log("🚀 Executing redirect to templates page...");
          try {
            navigate("/tender/resource-library/documents", { replace: true });
            console.log("✅ Navigate function called successfully");
          } catch (redirectError) {
            console.error("❌ Redirect failed:", redirectError);
            // Fallback: try without replace flag
            navigate("/tender/resource-library/documents");
          }
        }, 1500); // Increased delay to 1.5 seconds
        return;
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error",
        description: "Failed to save template",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <button
              onClick={() => {
                const createTemplateMode = sessionStorage.getItem(
                  "scopingai_createTemplateMode"
                );
                const returnToStep = sessionStorage.getItem(
                  "scopingai_returnToStep"
                );

                if (createTemplateMode === "true" && returnToStep === "2") {
                  // Clear the create template mode and return to template selection
                  sessionStorage.removeItem("scopingai_createTemplateMode");
                  sessionStorage.removeItem("scopingai_returnToStep");
                  navigate("/tender/documents/new");
                } else {
                  // Default behavior - go to document library
                  navigate("/tender/resource-library/documents");
                }
              }}
              className="flex items-center text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeft size={16} className="mr-2" />
              {sessionStorage.getItem("scopingai_createTemplateMode") === "true"
                ? "Back to Template Selection"
                : "Back to Templates"}
            </button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold">
                  {isEdit
                    ? "Edit Template"
                    : isImport
                    ? "Import MD Template"
                    : "Create Template"}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {isImport
                    ? "Edit and preview your imported Markdown file"
                    : isEdit
                    ? "Modify your existing template"
                    : "Create a new proposal template"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={
                    isLoading ||
                    !templateForm.name.trim() ||
                    !templateForm.content.trim()
                  }
                  className="gap-2"
                >
                  <Save size={16} />
                  {isLoading
                    ? "Saving..."
                    : isEdit
                    ? "Update Template"
                    : "Save Template"}
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Template Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={20} />
                  Template Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="template-name">Template Name *</Label>
                    <Input
                      id="template-name"
                      placeholder="Enter template name"
                      value={templateForm.name}
                      onChange={(e) =>
                        setTemplateForm({
                          ...templateForm,
                          name: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-category">Category</Label>
                    <Input
                      id="template-category"
                      placeholder="e.g., Web Development, Mobile App, etc."
                      value={templateForm.category}
                      onChange={(e) =>
                        setTemplateForm({
                          ...templateForm,
                          category: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-description">Description</Label>
                    <Input
                      id="template-description"
                      placeholder="Brief description of the template"
                      value={templateForm.description}
                      onChange={(e) =>
                        setTemplateForm({
                          ...templateForm,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* YooptaTemplateEditor - Section-based editing like regeneration page */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                  Template Content Editor
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                  Edit your template content with rich text editing and section-based management. Use ## for section headers (e.g., ## Project Overview).
                  </p>
                </CardHeader>
              <CardContent className="p-0">
                <div className="h-[700px] flex flex-col overflow-hidden">
                  <YooptaTemplateEditor
                    className="flex-1 min-h-0 overflow-hidden"
                    content={templateForm.content}
                    onChange={(content: string) => {
                      console.log("🔄 Template content changed:", {
                        length: content?.length || 0,
                        preview: content ? content.substring(0, 100) + '...' : 'no content'
                      });
                      // 🚨 FIXED: Use functional update to prevent stale state
                      setTemplateForm(prev => ({
                        ...prev,
                        content,
                      }));
                    }}
                    onSave={async () => {
                      try {
                        console.log("🚀 Template save triggered from YooptaTemplateEditor");
                        
                        if (!templateForm.name.trim() || !templateForm.content.trim()) {
                          toast({
                            title: "Error",
                            description: "Template name and content are required",
                            variant: "destructive",
                          });
                          return;
                        }

                        setIsLoading(true);

                        // Ensure content is properly formatted as markdown
                        const markdownContent = templateForm.content.trim();

                        // Extract sections from the markdown content based on headings
                        const extractedSections = extractSectionsFromMarkdown(markdownContent);

                        // Log the content and sections being saved for debugging
                        console.log("Saving markdown content:", markdownContent);
                        console.log("Extracted sections:", extractedSections);

                        if (isEdit && state?.editTemplate && user?.id) {
                          // Update existing template with auto-extracted sections
                          await updateCustomTemplate(state.editTemplate.id, {
                            name: templateForm.name.trim(),
                            description: templateForm.description.trim(),
                            content: markdownContent,
                            category: templateForm.category.trim(),
                            sections: extractedSections, // Auto-extracted sections
                          }, user.id);

                          toast({
                            title: "Success",
                            description: "Template updated successfully! You can continue editing.",
                          });
                        } else {
                          // Create new template with auto-extracted sections
                          const newTemplate = await createCustomTemplate({
                            name: templateForm.name.trim(),
                            description: templateForm.description.trim(),
                            content: markdownContent,
                            category: templateForm.category.trim(),
                            userId: user?.id || "",
                            sections: extractedSections, // Auto-extracted sections
                          });

                          toast({
                            title: "Success",
                            description: "Template created successfully! You can continue editing.",
                          });

                          // Check if we need to return to template selection with the new template
                          const createTemplateMode = sessionStorage.getItem(
                            "scopingai_createTemplateMode"
                          );
                          const returnToStep = sessionStorage.getItem("scopingai_returnToStep");

                          if (
                            createTemplateMode === "true" &&
                            returnToStep === "2" &&
                            newTemplate?.id
                          ) {
                            // Store the new template ID for selection
                            sessionStorage.setItem(
                              "selectedTemplateFromLibrary",
                              String(newTemplate.id)
                            );
                            // Clear the create template mode
                            sessionStorage.removeItem("scopingai_createTemplateMode");
                            sessionStorage.removeItem("scopingai_returnToStep");
                            // Navigate back to template selection step
                            navigate("/tender/documents/new");
                            return;
                          }
                        }

                        // 🚨 NO REDIRECT - Stay on the same page
                        console.log("✅ Template saved successfully - staying on current page");
                      } catch (error) {
                        console.error("❌ Template save failed:", error);
                        toast({
                          title: "Error",
                          description: "Failed to save template",
                          variant: "destructive",
                        });
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    readonly={false}
                    userId={user?.id}
                    isSaving={isLoading}
                      />
                    </div>
              </CardContent>
            </Card>

            {/* Legacy content - keep as fallback */}
            {false && (
                  <div className="border rounded-lg bg-background">
                    <div className="h-[600px] overflow-y-auto overflow-x-hidden p-6 scroll-smooth">
                      {templateForm.content ? (
                        <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-code:text-foreground prose-pre:text-foreground prose-blockquote:text-muted-foreground prose-li:text-foreground">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              // Custom text renderer to handle color syntax
                              text: ({ children }) => {
                                if (typeof children === "string") {
                                  // Handle color syntax like {red}(text) or {color}(text)
                                  const colorPattern = /\{(\w+)\}\(([^)]+)\)/g;
                                  const parts = [];
                                  let lastIndex = 0;
                                  let match;

                                  while (
                                    (match = colorPattern.exec(children)) !==
                                    null
                                  ) {
                                    // Add text before the match
                                    if (match.index > lastIndex) {
                                      parts.push(
                                        children.slice(lastIndex, match.index)
                                      );
                                    }

                                    const color = match[1].toLowerCase();
                                    const text = match[2];

                                    // Map colors to CSS values
                                    const colorMap = {
                                      red: "#ef4444",
                                      blue: "#3b82f6",
                                      green: "#10b981",
                                      yellow: "#f59e0b",
                                      purple: "#3790c5",
                                      orange: "#f97316",
                                      black: "#1f2937",
                                      gray: "#6b7280",
                                      grey: "#6b7280",
                                      pink: "#ec4899",
                                      indigo: "#6366f1",
                                      teal: "#14b8a6",
                                      cyan: "#06b6d4",
                                      lime: "#84cc16",
                                      emerald: "#10b981",
                                      rose: "#f43f5e",
                                      amber: "#f59e0b",
                                      violet: "#3790c5",
                                      fuchsia: "#d946ef",
                                      sky: "#0ea5e9",
                                      slate: "#64748b",
                                      zinc: "#71717a",
                                      neutral: "#737373",
                                      stone: "#78716c",
                                    };

                                    const colorValue = colorMap[color] || color;

                                    parts.push(
                                      <span
                                        key={match.index}
                                        style={{ color: colorValue }}
                                      >
                                        {text}
                                      </span>
                                    );

                                    lastIndex = match.index + match[0].length;
                                  }

                                  // Add remaining text
                                  if (lastIndex < children.length) {
                                    parts.push(children.slice(lastIndex));
                                  }

                                  return parts.length > 1 ? (
                                    <>{parts}</>
                                  ) : (
                                    children
                                  );
                                }
                                return children;
                              },
                              h1: ({ children }) => {
                                // Process color syntax in headers
                                const processColorSyntax = (text) => {
                                  if (typeof text === "string") {
                                    const colorPattern =
                                      /\{(\w+)\}\(([^)]+)\)/g;
                                    const parts = [];
                                    let lastIndex = 0;
                                    let match;

                                    while (
                                      (match = colorPattern.exec(text)) !== null
                                    ) {
                                      if (match.index > lastIndex) {
                                        parts.push(
                                          text.slice(lastIndex, match.index)
                                        );
                                      }

                                      const color = match[1].toLowerCase();
                                      const colorText = match[2];

                                      const colorMap = {
                                        red: "#ef4444",
                                        blue: "#3b82f6",
                                        green: "#10b981",
                                        yellow: "#f59e0b",
                                        purple: "#8b5cf6",
                                        orange: "#f97316",
                                        black: "#1f2937",
                                        gray: "#6b7280",
                                        grey: "#6b7280",
                                        pink: "#ec4899",
                                        indigo: "#6366f1",
                                        teal: "#14b8a6",
                                      };

                                      const colorValue =
                                        colorMap[color] || color;

                                      parts.push(
                                        <span
                                          key={match.index}
                                          style={{ color: colorValue }}
                                        >
                                          {colorText}
                                        </span>
                                      );

                                      lastIndex = match.index + match[0].length;
                                    }

                                    if (lastIndex < text.length) {
                                      parts.push(text.slice(lastIndex));
                                    }

                                    return parts.length > 1 ? parts : text;
                                  }
                                  return text;
                                };

                                return (
                                  <h1 className="text-3xl font-bold mb-6 text-foreground border-b-2 border-primary pb-3 mt-8 first:mt-0">
                                    {Array.isArray(children)
                                      ? children.map((child, index) =>
                                          typeof child === "string"
                                            ? processColorSyntax(child)
                                            : child
                                        )
                                      : processColorSyntax(children)}
                                  </h1>
                                );
                              },
                              h2: ({ children }) => {
                                const processColorSyntax = (text) => {
                                  if (typeof text === "string") {
                                    const colorPattern =
                                      /\{(\w+)\}\(([^)]+)\)/g;
                                    const parts = [];
                                    let lastIndex = 0;
                                    let match;

                                    while (
                                      (match = colorPattern.exec(text)) !== null
                                    ) {
                                      if (match.index > lastIndex) {
                                        parts.push(
                                          text.slice(lastIndex, match.index)
                                        );
                                      }

                                      const color = match[1].toLowerCase();
                                      const colorText = match[2];

                                      const colorMap = {
                                        red: "#ef4444",
                                        blue: "#3b82f6",
                                        green: "#10b981",
                                        yellow: "#f59e0b",
                                        purple: "#8b5cf6",
                                        orange: "#f97316",
                                        black: "#1f2937",
                                        gray: "#6b7280",
                                        grey: "#6b7280",
                                        pink: "#ec4899",
                                        indigo: "#6366f1",
                                        teal: "#14b8a6",
                                      };

                                      const colorValue =
                                        colorMap[color] || color;

                                      parts.push(
                                        <span
                                          key={match.index}
                                          style={{ color: colorValue }}
                                        >
                                          {colorText}
                                        </span>
                                      );

                                      lastIndex = match.index + match[0].length;
                                    }

                                    if (lastIndex < text.length) {
                                      parts.push(text.slice(lastIndex));
                                    }

                                    return parts.length > 1 ? parts : text;
                                  }
                                  return text;
                                };

                                return (
                                  <h2 className="text-2xl font-semibold mb-4 mt-8 text-foreground border-l-4 border-primary pl-4">
                                    {Array.isArray(children)
                                      ? children.map((child, index) =>
                                          typeof child === "string"
                                            ? processColorSyntax(child)
                                            : child
                                        )
                                      : processColorSyntax(children)}
                                  </h2>
                                );
                              },
                              h3: ({ children }) => {
                                const processColorSyntax = (text) => {
                                  if (typeof text === "string") {
                                    const colorPattern =
                                      /\{(\w+)\}\(([^)]+)\)/g;
                                    const parts = [];
                                    let lastIndex = 0;
                                    let match;

                                    while (
                                      (match = colorPattern.exec(text)) !== null
                                    ) {
                                      if (match.index > lastIndex) {
                                        parts.push(
                                          text.slice(lastIndex, match.index)
                                        );
                                      }

                                      const color = match[1].toLowerCase();
                                      const colorText = match[2];

                                      const colorMap = {
                                        red: "#ef4444",
                                        blue: "#3b82f6",
                                        green: "#10b981",
                                        yellow: "#f59e0b",
                                        purple: "#8b5cf6",
                                        orange: "#f97316",
                                        black: "#1f2937",
                                        gray: "#6b7280",
                                        grey: "#6b7280",
                                      };

                                      const colorValue =
                                        colorMap[color] || color;

                                      parts.push(
                                        <span
                                          key={match.index}
                                          style={{ color: colorValue }}
                                        >
                                          {colorText}
                                        </span>
                                      );

                                      lastIndex = match.index + match[0].length;
                                    }

                                    if (lastIndex < text.length) {
                                      parts.push(text.slice(lastIndex));
                                    }

                                    return parts.length > 1 ? parts : text;
                                  }
                                  return text;
                                };

                                return (
                                  <h3 className="text-xl font-medium mb-3 mt-6 text-foreground">
                                    {Array.isArray(children)
                                      ? children.map((child, index) =>
                                          typeof child === "string"
                                            ? processColorSyntax(child)
                                            : child
                                        )
                                      : processColorSyntax(children)}
                                  </h3>
                                );
                              },
                              h4: ({ children }) => {
                                const processColorSyntax = (text) => {
                                  if (typeof text === "string") {
                                    const colorPattern =
                                      /\{(\w+)\}\(([^)]+)\)/g;
                                    const parts = [];
                                    let lastIndex = 0;
                                    let match;

                                    while (
                                      (match = colorPattern.exec(text)) !== null
                                    ) {
                                      if (match.index > lastIndex) {
                                        parts.push(
                                          text.slice(lastIndex, match.index)
                                        );
                                      }

                                      const color = match[1].toLowerCase();
                                      const colorText = match[2];

                                      const colorMap = {
                                        red: "#ef4444",
                                        blue: "#3b82f6",
                                        green: "#10b981",
                                        yellow: "#f59e0b",
                                        purple: "#8b5cf6",
                                        orange: "#f97316",
                                        black: "#1f2937",
                                        gray: "#6b7280",
                                        grey: "#6b7280",
                                      };

                                      const colorValue =
                                        colorMap[color] || color;

                                      parts.push(
                                        <span
                                          key={match.index}
                                          style={{ color: colorValue }}
                                        >
                                          {colorText}
                                        </span>
                                      );

                                      lastIndex = match.index + match[0].length;
                                    }

                                    if (lastIndex < text.length) {
                                      parts.push(text.slice(lastIndex));
                                    }

                                    return parts.length > 1 ? parts : text;
                                  }
                                  return text;
                                };

                                return (
                                  <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">
                                    {Array.isArray(children)
                                      ? children.map((child, index) =>
                                          typeof child === "string"
                                            ? processColorSyntax(child)
                                            : child
                                        )
                                      : processColorSyntax(children)}
                                  </h4>
                                );
                              },
                              p: ({ children }) => (
                                <p className="mb-4 text-foreground leading-relaxed text-base">
                                  {children}
                                </p>
                              ),
                              ul: ({ children }) => (
                                <ul className="list-disc list-outside ml-6 mb-4 space-y-2 text-foreground">
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol className="list-decimal list-outside ml-6 mb-4 space-y-2 text-foreground">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="text-foreground leading-relaxed">
                                  {children}
                                </li>
                              ),
                              blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950/20 pl-6 py-4 italic mb-4 text-blue-800 dark:text-blue-200 rounded-r-lg">
                                  {children}
                                </blockquote>
                              ),
                              code: ({ children, className }) => {
                                const isInline = !className;
                                return isInline ? (
                                  <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm font-mono text-red-600 dark:text-red-400">
                                    {children}
                                  </code>
                                ) : (
                                  <code className={className}>{children}</code>
                                );
                              },
                              pre: ({ children }) => (
                                <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4 border">
                                  {children}
                                </pre>
                              ),
                              table: ({ children }) => (
                                <div className="overflow-x-auto mb-6 rounded-lg border border-gray-300 dark:border-gray-600">
                                  <table className="min-w-full border-collapse bg-white dark:bg-gray-800">
                                    {children}
                                  </table>
                                </div>
                              ),
                              thead: ({ children }) => (
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  {children}
                                </thead>
                              ),
                              tbody: ({ children }) => (
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                  {children}
                                </tbody>
                              ),
                              tr: ({ children }) => (
                                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  {children}
                                </tr>
                              ),
                              th: ({ children }) => (
                                <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-gray-100 dark:bg-gray-700 font-semibold text-left text-foreground">
                                  {children}
                                </th>
                              ),
                              td: ({ children }) => (
                                <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-foreground">
                                  {children}
                                </td>
                              ),
                              strong: ({ children }) => (
                                <strong className="font-bold text-foreground">
                                  {children}
                                </strong>
                              ),
                              em: ({ children }) => (
                                <em className="italic text-foreground">
                                  {children}
                                </em>
                              ),
                              a: ({ children, href }) => (
                                <a
                                  href={href}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {children}
                                </a>
                              ),
                              hr: () => (
                                <hr className="my-8 border-t-2 border-gray-300 dark:border-gray-600" />
                              ),
                              del: ({ children }) => (
                                <del className="line-through text-gray-500 dark:text-gray-400">
                                  {children}
                                </del>
                              ),
                              input: ({ checked, type }) => {
                                if (type === "checkbox") {
                                  return (
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      readOnly
                                      className="mr-2 rounded"
                                    />
                                  );
                                }
                                return null;
                              },
                              img: ({ src, alt }) => (
                                <img
                                  src={src}
                                  alt={alt}
                                  className="max-w-full h-auto rounded-lg border border-gray-300 dark:border-gray-600 my-4"
                                />
                              ),
                              sup: ({ children }) => (
                                <sup className="text-xs">{children}</sup>
                              ),
                              sub: ({ children }) => (
                                <sub className="text-xs">{children}</sub>
                              ),
                            }}
                          >
                            {templateForm.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                          <div className="text-center">
                            <FileText
                              size={48}
                              className="mx-auto mb-4 opacity-50"
                            />
                            <p>Start typing to see the preview</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
          </div>
        </div>
      </div>
    </ScopingAILayout>
  );
}
